/**
 * VoiceWidget - переиспользуемый UI компонент голосового/чат виджета
 *
 * Чистый UI без логики подключения к провайдерам (LiveKit).
 * Логика подключается снаружи через callbacks.
 *
 * Режимы (mode):
 * - collapsed: свёрнутая кнопка с иконками (CollapsedBar)
 * - voice: голосовой бар с waveform (VoiceBar)
 * - chat: чат с сообщениями и контролами (ChatPanel)
 *
 * @example
 * // Collapsed mode
 * <VoiceWidget
 *   mode="collapsed"
 *   title="Ask us"
 *   onMicClick={() => setMode('voice')}
 *   onChatClick={() => setMode('chat')}
 * />
 *
 * // Chat mode with custom controls
 * <VoiceWidget
 *   mode="chat"
 *   messages={messages}
 *   isSessionActive={true}
 *   renderControls={() => <VoiceBar ... />}
 * />
 */

import PropTypes from 'prop-types';
import { CollapsedBar } from './CollapsedBar';
import { VoiceBar } from './VoiceBar';
import { ChatPanel } from './ChatPanel';
import { getThemeStyles } from './themes';
import { DEFAULT_TERMS_URL, DEFAULT_PRIVACY_URL } from './legalDefaults';
import { openWhatsApp } from '../../utils/whatsapp';
import { openTelegram } from '../../utils/telegram';
import { languagePropType } from './utils';
import './widget-tokens.css';

/**
 * @typedef {'collapsed' | 'voice' | 'chat'} WidgetMode
 *
 * @typedef {Object} Message
 * @property {string} id
 * @property {'user' | 'assistant' | 'system'} role
 * @property {string} text
 * @property {Date} [timestamp]
 *
 * @typedef {Object} VoiceWidgetProps
 * @property {WidgetMode} mode - Текущий режим виджета
 * @property {boolean} [isSpeaking] - AI сейчас говорит (анимация эквалайзера)
 * @property {boolean} [isMuted] - Микрофон выключен
 * @property {boolean} [speakerMuted] - Динамик выключен
 * @property {boolean} [isSessionActive] - Сессия активна (зелёная точка)
 * @property {Message[]} [messages] - Сообщения чата
 * @property {string} [title] - Заголовок (default: "Ask us")
 * @property {boolean} [showWhatsApp] - Показывать кнопку WhatsApp
 * @property {string} [whatsAppNumber] - Номер WhatsApp для ссылки
 * @property {boolean} [showTelegram] - Показывать кнопку Telegram (#1493)
 * @property {string} [telegramHandle] - Telegram-хендл (`name`, `@name`, `t.me/name`)
 * @property {string} [errorMessage] - Сообщение об ошибке
 * @property {string} [widgetState] - Состояние окна: 'minimized' | 'normal' | 'maximized'
 * @property {number} [width] - Ширина (для chat mode)
 * @property {number} [height] - Высота (для chat mode)
 * @property {Function} [onMicClick] - Клик на микрофон (переход в voice mode)
 * @property {Function} [onChatClick] - Клик на чат (переход в chat mode)
 * @property {Function} [onWhatsAppClick] - Клик на WhatsApp
 * @property {Function} [onSendMessage] - Отправка текстового сообщения
 * @property {Function} [onMicToggle] - Toggle микрофона
 * @property {Function} [onSpeakerToggle] - Toggle динамика
 * @property {Function} [onClose] - Закрытие виджета
 * @property {Function} [onMinimize] - Сворачивание виджета
 * @property {Function} [onMaximize] - Максимизация виджета
 * @property {Function} [onRestore] - Восстановление виджета
 * @property {Function} [onCollapseToVoice] - Свернуть чат в voice/collapsed mode
 * @property {Function} [renderControls] - Render prop для нижних контролов (chat mode)
 * @property {boolean} [isDraggable] - Можно ли перетаскивать
 * @property {Function} [onDragStart] - Начало перетаскивания
 * @property {string} [className] - Дополнительные CSS классы
 * @property {Object} [theme] - Кастомные цвета { primaryColor, accentColor }
 */

export const VoiceWidget = ({
  mode = 'collapsed',
  isSpeaking = false,
  isMuted = false,
  speakerMuted = false,
  isSessionActive = false,
  connectionStatus, // Issue #566: 'connecting' | 'connected' | 'disconnected' (passed to VoiceBar)
  messages = [],
  title = 'Ask us',
  showWhatsApp = false,
  whatsAppNumber = '',
  showTelegram = false,
  telegramHandle = '',
  errorMessage = '',
  widgetState = 'normal',
  width,
  height,
  onMicClick,
  onChatClick,
  onWhatsAppClick,
  onModeChange,
  onSendMessage,
  onMicToggle,
  onSpeakerToggle,
  showSpeakerButton = false,
  showMicButton = false,
  onClose,
  onMinimize,
  onMaximize,
  onRestore,
  onCollapseToVoice,
  onExpandToChat,
  renderControls,
  // Voice in chat
  showVoiceControls = false,
  onVoiceClose,
  isUserSpeaking = false,
  userAudioLevel = 0,
  agentAudioLevel = 0,
  isDraggable = false,
  onDragStart,
  className = '',
  theme = {},
  isMobile = false,
  isModeChanging = false, // Индикатор переключения режима
  showConsentBanner = false,
  termsUrl = DEFAULT_TERMS_URL,
  privacyUrl = DEFAULT_PRIVACY_URL,
  autoScroll = true,
  // Issue #752: Auto-suggestions
  suggestions = [],
  suggestionsLoading = false,
  suggestionsConfig,
  onSuggestionSelect,
  // Mini collapsed state (mobile only)
  isMini = false,
  onExpand,
  // #1353 — text-only дилер: ни микрофона в collapsed-баре, ни voice/chat
  // тумблера в шапке чата. Default true — поведение для всех остальных.
  voiceEnabled = true,
  // #1417: язык подписей виджета. Передаёт только витрина; у дилера 'en'.
  language = 'en',
  // Язык строк интерфейса виджета, добавленных в #1431 (согласие, кнопки,
  // «Powered by»): на своей витрине — её язык, у дилеров всегда en. Старые
  // строки (плейсхолдер, «End conversation») идут по `language`, как раньше.
  chromeLanguage = 'en',
}) => {
  // Применяем accent bg стили только для voice и collapsed режимов
  const isAccentBgMode = theme.accent === 'bg' && (mode === 'voice' || mode === 'collapsed');
  // Accent buttons применяется только к collapsed
  const isAccentButtons = theme.accent === 'buttons' && mode === 'collapsed';
  const isGlassMode = theme.glass === true;
  const style = getThemeStyles(theme, { isAccentMode: isAccentBgMode });

  const handleWhatsAppClick = () => {
    if (onWhatsAppClick) {
      onWhatsAppClick();
    } else {
      openWhatsApp({ phone: whatsAppNumber, context: 'voice_widget' });
    }
  };

  const handleTelegramClick = () => {
    openTelegram({ handle: telegramHandle, context: 'voice_widget' });
  };

  return (
    <div className={`voice-widget ${className}`} style={style}>
      {mode === 'collapsed' && (
        <CollapsedBar
          language={chromeLanguage}
          title={title}
          isSpeaking={isSpeaking}
          showWhatsApp={showWhatsApp}
          showTelegram={showTelegram}
          showVoiceButton={voiceEnabled}
          onMicClick={onMicClick}
          onChatClick={onChatClick}
          onWhatsAppClick={handleWhatsAppClick}
          onTelegramClick={handleTelegramClick}
          primaryColor={theme.primaryColor}
          theme={theme}
          isAccentMode={isAccentBgMode}
          isAccentButtons={isAccentButtons}
          glassEffect={isGlassMode}
          isMini={isMini}
          onExpand={onExpand}
        />
      )}

      {mode === 'voice' && (
        <VoiceBar
          language={language}
          isSpeaking={isSpeaking}
          isUserSpeaking={isUserSpeaking}
          connectionStatus={connectionStatus}
          onClose={onClose}
          onExpandToChat={onExpandToChat}
          glassEffect={isGlassMode}
          primaryColor={theme.primaryColor}
          theme={theme}
          isAccentMode={isAccentBgMode}
        />
      )}

      {mode === 'chat' && (
        <ChatPanel
          language={language}
          chromeLanguage={chromeLanguage}
          title={title}
          isSessionActive={isSessionActive}
          isSpeaking={isSpeaking}
          showModeToggle={voiceEnabled}
          currentMode={showVoiceControls ? 'voice' : 'chat'}
          onModeChange={onModeChange}
          speakerMuted={speakerMuted}
          onSpeakerToggle={onSpeakerToggle}
          showSpeakerButton={showSpeakerButton}
          micMuted={isMuted}
          onMicToggle={onMicToggle}
          showMicButton={showMicButton}
          widgetState={widgetState}
          onMinimize={onMinimize}
          onMaximize={onMaximize}
          onRestore={onRestore}
          onClose={onClose}
          onCollapseToVoice={onCollapseToVoice}
          isDraggable={isDraggable}
          onDragStart={onDragStart}
          messages={messages}
          autoScroll={autoScroll}
          emptyText=""
          onSendMessage={onSendMessage}
          errorMessage={errorMessage}
          renderControls={renderControls}
          // Voice in chat
          showVoiceControls={showVoiceControls}
          onVoiceClose={onVoiceClose}
          isUserSpeaking={isUserSpeaking}
          userAudioLevel={userAudioLevel}
          agentAudioLevel={agentAudioLevel}
          primaryColor={theme.primaryColor}
          theme={theme}
          glassEffect={isGlassMode}
          isAccentMode={theme.accent !== 'off' && showVoiceControls}
          width={width}
          height={height}
          isMobile={isMobile}
          isModeChanging={isModeChanging}
          showConsentBanner={showConsentBanner}
          termsUrl={termsUrl}
          privacyUrl={privacyUrl}
          // Issue #752: Auto-suggestions
          suggestions={suggestions}
          suggestionsLoading={suggestionsLoading}
          suggestionsConfig={suggestionsConfig}
          onSuggestionSelect={onSuggestionSelect}
        />
      )}
    </div>
  );
};

VoiceWidget.propTypes = {
  mode: PropTypes.oneOf(['collapsed', 'voice', 'chat']),
  isSpeaking: PropTypes.bool,
  isMuted: PropTypes.bool,
  speakerMuted: PropTypes.bool,
  isSessionActive: PropTypes.bool,
  connectionStatus: PropTypes.oneOf(['connecting', 'connected', 'disconnected']),
  messages: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    role: PropTypes.oneOf(['user', 'assistant', 'system']).isRequired,
    text: PropTypes.string.isRequired,
    timestamp: PropTypes.instanceOf(Date),
  })),
  autoScroll: PropTypes.bool,
  title: PropTypes.string,
  showWhatsApp: PropTypes.bool,
  whatsAppNumber: PropTypes.string,
  showTelegram: PropTypes.bool,
  telegramHandle: PropTypes.string,
  errorMessage: PropTypes.string,
  widgetState: PropTypes.oneOf(['minimized', 'normal', 'maximized']),
  width: PropTypes.number,
  height: PropTypes.number,
  onMicClick: PropTypes.func,
  onChatClick: PropTypes.func,
  onWhatsAppClick: PropTypes.func,
  onModeChange: PropTypes.func,
  onSendMessage: PropTypes.func,
  onMicToggle: PropTypes.func,
  onSpeakerToggle: PropTypes.func,
  showSpeakerButton: PropTypes.bool,
  showMicButton: PropTypes.bool,
  onClose: PropTypes.func,
  onMinimize: PropTypes.func,
  onMaximize: PropTypes.func,
  onRestore: PropTypes.func,
  onCollapseToVoice: PropTypes.func,
  onExpandToChat: PropTypes.func,
  renderControls: PropTypes.func,
  showVoiceControls: PropTypes.bool,
  onVoiceClose: PropTypes.func,
  isUserSpeaking: PropTypes.bool,
  userAudioLevel: PropTypes.number,
  agentAudioLevel: PropTypes.number,
  isDraggable: PropTypes.bool,
  onDragStart: PropTypes.func,
  className: PropTypes.string,
  theme: PropTypes.object,
  isMobile: PropTypes.bool,
  isModeChanging: PropTypes.bool,
  showConsentBanner: PropTypes.bool,
  termsUrl: PropTypes.string,
  privacyUrl: PropTypes.string,
  // Issue #752: Auto-suggestions
  suggestions: PropTypes.arrayOf(PropTypes.string),
  suggestionsLoading: PropTypes.bool,
  suggestionsConfig: PropTypes.object,
  onSuggestionSelect: PropTypes.func,
  isMini: PropTypes.bool,
  voiceEnabled: PropTypes.bool,
  language: languagePropType,
  chromeLanguage: languagePropType,
  onExpand: PropTypes.func,
};

// Re-export sub-components for direct use
export { CollapsedBar } from './CollapsedBar';
export { VoiceBar } from './VoiceBar';
export { ChatPanel } from './ChatPanel';
export { MessagesArea } from './MessagesArea';
export { WidgetHeader } from './WidgetHeader';
export { Equalizer } from './Equalizer';

// Re-export themes
export { themes, getTheme, getThemeStyles } from './themes';

// Re-export connected version
export { VoiceWidgetConnected } from './VoiceWidgetConnected';

export default VoiceWidget;
